// The library shelf's presentation layer. The corpus catalog knows books and
// chapters; it does NOT know that a book ships as more than one physical
// cover, or what that cover is titled — the Odyssey is one catalog book but
// three printed volumes ("Mentor and the Staff", "The Cyclops' Curse", "The
// King in Rags"), each with its own cover art. The backend DOES now own each
// volume's chapter BOUNDARIES (`convex/model/bookConfig.ts`'s `volumes`,
// needed for pricing/entitlement correctness — see `volumeChapters` below),
// but it still owns nothing about a volume's PRESENTATION: its cover art, its
// cover title, its `volumeLabel`, or where it sits on the shelf. That stays
// static config here, rather than in the catalog schema.

import type {
  CatalogBook,
  CatalogMatter,
} from "@bindery/shared/corpus/catalog";
import { getBookConfig } from "@bindery/convex-retells/model/bookConfig";
import { shelfBookOrder } from "@bindery/convex-retells/model/shelfOrder";
import { bookVolumes } from "@bindery/convex-retells/model/volume";
import { readingOrder, unitSegment, type ReadingUnit } from "./reading-order";

/**
 * `bookConfig.ts`'s chapter range for volume `volumeNumber` of `bookSlug` —
 * the single source `ShelfEdition.chapters` reads below rather than
 * retyping. Throws at MODULE LOAD (every call site is a `SHELF` literal, so
 * this runs the moment the module is imported) so a typo in the volume
 * number is a build break, not a runtime surprise.
 */
function volumeChapters(
  bookSlug: string,
  volumeNumber: number,
): { readonly from: number; readonly to: number } {
  const volume = bookVolumes(getBookConfig(bookSlug)).find(
    (v) => v.volumeNumber === volumeNumber,
  );
  if (!volume) {
    throw new Error(
      `shelf.ts: "${bookSlug}" has no configured volume ${String(volumeNumber)}`,
    );
  }
  return volume.chapters;
}

/**
 * One physical edition on the shelf: a whole book, or one volume of one.
 * Every field (including the nested `coverSize`/`chapters` objects) is
 * `readonly` — `SHELF` below is module-global config every render reads, and
 * `readonly ShelfEdition[]` alone only freezes the array's slots, not the
 * elements: `SHELF[1].chapters.from = 9` would otherwise still type-check
 * and mutate shared state (FP policy: treat data as immutable).
 */
export interface ShelfEdition {
  /** Stable React key / sheet id. */
  readonly id: string;
  /** Catalog book slug this edition draws its chapters from. */
  readonly bookSlug: string;
  /** Cover art path under `public/` (served from the site root). */
  readonly cover: string;
  /** Cover width/height in px, for intrinsic sizing (avoids layout shift). */
  readonly coverSize: { readonly width: number; readonly height: number };
  /** The title printed on the cover, which is NOT the catalog title for a volume. */
  readonly coverTitle: string;
  /** "Volume One" etc., absent for a single-volume book. */
  readonly volumeLabel?: string;
  /**
   * Inclusive chapter range this edition contains. Absent = the whole book.
   */
  readonly chapters?: { readonly from: number; readonly to: number };
}

/** The shelf, in display order — front to back, left to right. */
export const SHELF: readonly ShelfEdition[] = [
  {
    id: "peter-pan",
    bookSlug: "peter-pan",
    cover: "/peter-pan.webp",
    coverSize: { width: 788, height: 1239 },
    coverTitle: "Peter Pan",
  },
  {
    id: "odyssey-1",
    bookSlug: "odyssey",
    cover: "/the-odyssey-01.webp",
    coverSize: { width: 788, height: 1238 },
    coverTitle: "Mentor and the Staff",
    volumeLabel: "Volume One",
    chapters: volumeChapters("odyssey", 1),
  },
  {
    id: "odyssey-2",
    bookSlug: "odyssey",
    cover: "/the-odyssey-02.webp",
    coverSize: { width: 789, height: 1238 },
    coverTitle: "The Cyclops' Curse",
    volumeLabel: "Volume Two",
    chapters: volumeChapters("odyssey", 2),
  },
  {
    id: "odyssey-3",
    bookSlug: "odyssey",
    cover: "/the-odyssey-03.webp",
    coverSize: { width: 788, height: 1238 },
    coverTitle: "The King in Rags",
    volumeLabel: "Volume Three",
    chapters: volumeChapters("odyssey", 3),
  },
  {
    id: "heidi",
    bookSlug: "heidi",
    cover: "/heidi.webp",
    coverSize: { width: 788, height: 1239 },
    coverTitle: "Heidi",
  },
  {
    id: "alice",
    bookSlug: "alice",
    cover: "/alice.webp",
    coverSize: { width: 788, height: 1239 },
    coverTitle: "Alice's Adventures in Wonderland",
  },
  // Don Quixote's three volumes split on the source repo's own narrative
  // boundaries (book.yaml `volumes:` 1-3), not on an even chapter count.
  // `bookConfig.ts` configures all eight volumes of the novel's 126 chapters
  // (the complete-title bundle preorders the rest), but only these first
  // three have cover art — volumes four through eight are configured and
  // priceable ahead of their covers, which is a content task, not a pricing
  // one; see the note in `bookConfig.ts` about what shipping them would take.
  {
    id: "don-quixote-1",
    bookSlug: "don-quixote",
    cover: "/don-quixote-01.webp",
    coverSize: { width: 788, height: 1239 },
    coverTitle: "The Knighting at the Inn",
    volumeLabel: "Volume One",
    chapters: volumeChapters("don-quixote", 1),
  },
  {
    id: "don-quixote-2",
    bookSlug: "don-quixote",
    cover: "/don-quixote-02.webp",
    coverSize: { width: 788, height: 1239 },
    coverTitle: "The Shepherdess's Answer",
    volumeLabel: "Volume Two",
    chapters: volumeChapters("don-quixote", 2),
  },
  {
    id: "don-quixote-3",
    bookSlug: "don-quixote",
    cover: "/don-quixote-03.webp",
    coverSize: { width: 788, height: 1239 },
    coverTitle: "The Ragged Knight",
    volumeLabel: "Volume Three",
    chapters: volumeChapters("don-quixote", 3),
  },
  {
    id: "nutcracker",
    bookSlug: "nutcracker",
    cover: "/nutcracker.webp",
    coverSize: { width: 788, height: 1239 },
    coverTitle: "The Nutcracker and the Mouse King",
  },
  // One cover for all 28 divisions, though the source repo divides Frankenstein
  // into six narrative volumes. Splitting it is a later decision and may not be
  // all six, so nothing here encodes those boundaries yet — adding `chapters`
  // ranges and volume covers is the whole of what it would take.
  {
    id: "frankenstein",
    bookSlug: "frankenstein",
    cover: "/frankenstein.webp",
    coverSize: { width: 789, height: 1239 },
    coverTitle: "Frankenstein",
  },
];

/**
 * Cover art for a book that is drawn but not yet retold — no catalog entry.
 * Deeply `readonly` for the same reason as `ShelfEdition` above.
 */
export interface ForthcomingEdition {
  readonly id: string;
  readonly cover: string;
  readonly coverSize: { readonly width: number; readonly height: number };
  readonly coverTitle: string;
}

/**
 * Covers for books with no catalog entry yet — rendered as dimmed
 * "forthcoming" tiles rather than linked editions.
 */
export const FORTHCOMING: readonly ForthcomingEdition[] = [];

/**
 * Split a book's reading order across `editions`, one bucket per edition, in
 * the given order.
 *
 * Deliberately whole-shelf rather than one-edition-at-a-time: what a cover
 * binds is a decision about the book, not about that cover. Ask each volume
 * in isolation "do I hold the front matter?" and the answers only add up when
 * the catalog's chapters happen to line up with the shelf's declared ranges —
 * two volumes each claim the afterword when the last range overshoots the
 * catalog (the Odyssey's dormant paywall: volumes declared through 24, only
 * 1–12 seeded), and none claims the foreword when the first chapter sits above
 * the first range. Deciding here, once, makes
 * `shelfUnits(book, editions).flat() ≡ readingOrder(book)` hold by
 * construction for any catalog.
 */
export function shelfUnits(
  book: CatalogBook,
  editions: readonly ShelfEdition[],
): ReadingUnit[][] {
  const order = readingOrder(book);

  // Which volume owns a chapter: the last one that opens at or before it
  // (editions arrive in shelf order, ranges ascending; an unranged edition is
  // the whole book, so it opens before everything). Only `from` decides —
  // `to` is caption copy, and letting it decide is what drops a chapter that
  // falls past every declared range or into a gap between two.
  const opensAt = (e: ShelfEdition) => e.chapters?.from ?? -Infinity;
  // A backwards scan, not `findLastIndex` (ES2023 — past the chrome80/safari14
  // BROWSER_FLOOR).
  const ownerOf = (number: number) => {
    for (let i = editions.length - 1; i >= 0; i--) {
      const edition = editions[i];
      if (edition && opensAt(edition) <= number) return i;
    }
    return 0;
  };

  // The volumes that actually hold a chapter, in reading order. A book no
  // volume holds a chapter of (one with no chapters at all, or none the shelf
  // reaches) still has to put its whole-book matter somewhere: the first cover
  // takes it, rather than every cover taking a copy.
  const chapterOwners = order.flatMap((u) =>
    u.kind === "chapter" ? [ownerOf(u.number)] : [],
  );
  const frontOwner = chapterOwners[0] ?? 0;
  const backOwner = chapterOwners[chapterOwners.length - 1] ?? 0;

  // Whole-book matter rides with the volumes that hold the first and last
  // chapters; ANCHORED matter rides with the volume holding the chapter it is
  // anchored to, which is the whole point of the anchor — the Odyssey's
  // volume-one afterword names chapter 4 and so binds to volume one, where a
  // "back" placement would have sent it to the end of the book. No `default`:
  // a new placement variant must be given a volume here, not silently filed
  // under the front.
  const ownerOfMatter = (placement: CatalogMatter["placement"]): number => {
    switch (placement.at) {
      case "front":
        return frontOwner;
      case "back":
        return backOwner;
      case "before":
      case "after":
        return ownerOf(placement.chapter);
    }
  };
  const ownerBySlug = new Map(
    book.matter.map((m) => [m.slug, ownerOfMatter(m.placement)]),
  );

  // Partitioning `readingOrder` itself — rather than re-deriving each group's
  // members from the catalog — is what keeps
  // `shelfUnits(book, editions).flat() ≡ readingOrder(book)` true by
  // construction: every unit is filed exactly once, in the order it already
  // had. It holds because the owner index never decreases along reading order
  // (front matter takes the first chapter's volume, anchored matter takes its
  // own chapter's, chapters ascend, back matter takes the last chapter's).
  const ownerOfUnit = (u: ReadingUnit): number =>
    u.kind === "chapter"
      ? ownerOf(u.number)
      : // Every matter unit in `order` came from `book.matter`, so this lookup
        // always hits; the fallback only keeps the function total.
        (ownerBySlug.get(u.slug) ?? frontOwner);

  return editions.map((_, i) => order.filter((u) => ownerOfUnit(u) === i));
}

/** The editions on the shelf that belong to `bookSlug`, in shelf order. */
export function editionsFor(bookSlug: string): readonly ShelfEdition[] {
  return SHELF.filter((e) => e.bookSlug === bookSlug);
}

/**
 * Every book on the shelf, in the order `SHELF` stands them in — the DEFAULT
 * shelf order, and the list an admin's saved order is reconciled against
 * (`convex/model/shelfOrder.ts`).
 */
export const SHELF_BOOK_SLUGS: readonly string[] = [
  ...new Set(SHELF.map((e) => e.bookSlug)),
];

/**
 * `SHELF`, with its books reordered by `bookOrder` — the admin's saved order,
 * or `null`/`undefined` for "none saved, or not loaded yet" (both ship the
 * default). Reordering is BY BOOK: a book's own editions keep their relative
 * order, so the Odyssey's three volumes travel together and in sequence
 * whatever the shelf order says. See `convex/model/shelfOrder.ts` for why that
 * is a correctness constraint rather than a nicety.
 */
export function shelfEditions(
  bookOrder?: readonly string[] | null,
): readonly ShelfEdition[] {
  if (bookOrder === null || bookOrder === undefined) return SHELF;
  return shelfBookOrder(SHELF_BOOK_SLUGS, bookOrder).flatMap((slug) =>
    editionsFor(slug),
  );
}

/** One cover as the library renders it: art, catalog book, and what it binds. */
export interface ShelvedEdition {
  readonly edition: ShelfEdition;
  readonly book: CatalogBook;
  /**
   * The units this cover binds — empty when the catalog holds none of the
   * chapters it declares (an unseeded volume), which the library shelves as
   * forthcoming rather than as a cover with nothing behind it.
   */
  readonly units: readonly ReadingUnit[];
}

/**
 * The shelf as rendered: every edition whose book is in the catalog, in shelf
 * order, each carrying the units it binds. Units are assigned a book at a time
 * (see `shelfUnits`) and only then re-sorted into shelf order, so the two
 * concerns can't be confused at the call site.
 *
 * `bookOrder` is the admin's saved order (`convex/shelf.ts`), applied by
 * `shelfEditions`; omit it — or pass the `undefined` a still-loading Convex
 * query gives you — for the shipped order.
 */
export function shelveCatalog(
  books: readonly CatalogBook[],
  bookOrder?: readonly string[] | null,
): ShelvedEdition[] {
  const byEditionId = new Map(
    books.flatMap((book) => {
      const editions = editionsFor(book.slug);
      return shelfUnits(book, editions).map(
        (units, i) => [editions[i]!.id, { book, units }] as const,
      );
    }),
  );
  return shelfEditions(bookOrder).flatMap((edition) => {
    const shelved = byEditionId.get(edition.id);
    return shelved ? [{ edition, ...shelved }] : [];
  });
}

/**
 * One book's covers, together — a single-volume book is a series of one.
 *
 * The library renders a SERIES per grid cell rather than an edition: three
 * volumes of the Odyssey stand as one stacked spine-on cover that opens into
 * its volumes, which is only coherent if the shelf hands them over as a group.
 */
export interface ShelvedSeries {
  /** Stable React key — the book slug, which is unique across the shelf. */
  readonly bookSlug: string;
  readonly book: CatalogBook;
  /** This book's covers, in reading order (volume one first). */
  readonly editions: readonly ShelvedEdition[];
}

/**
 * Group a shelved catalog by book, preserving shelf order.
 *
 * Groups by SLUG rather than by adjacency, so a `SHELF` that ever interleaved
 * two books' volumes would still produce one series each — but the order of the
 * series themselves is first-appearance order, which for the shelf we ship (and
 * for any order `shelfEditions` produces, since it emits a book's editions
 * together) is exactly shelf order.
 */
export function groupIntoSeries(
  shelved: readonly ShelvedEdition[],
): ShelvedSeries[] {
  const byBook = new Map<string, ShelvedEdition[]>();
  for (const entry of shelved) {
    const slug = entry.edition.bookSlug;
    const group = byBook.get(slug);
    if (group) group.push(entry);
    else byBook.set(slug, [entry]);
  }
  return [...byBook].map(([bookSlug, editions]) => ({
    bookSlug,
    book: editions[0]!.book,
    editions,
  }));
}

/**
 * The same volumes, with the ones `isRead` accepts moved to the back.
 *
 * Stable within each half, so unread volumes stay in reading order and finished
 * ones keep theirs behind them: a reader returning to the Odyssey sees the
 * volume they are up to at the front of the stack, without anything being
 * hidden. Pure, and takes a predicate rather than the read-mark store, so the
 * shelf never learns how read state is persisted.
 */
export function sinkRead(
  editions: readonly ShelvedEdition[],
  isRead: (entry: ShelvedEdition) => boolean,
): ShelvedEdition[] {
  return [
    ...editions.filter((e) => !isRead(e)),
    ...editions.filter((e) => isRead(e)),
  ];
}

/**
 * The chapter a run of units ENDS on, or `null` for a run with no chapters in
 * it at all. This is the number a volume's read mark is stored under — see
 * `convex/model/readMark.ts` for why a chapter number rather than an edition id.
 *
 * Derived from the units the edition actually BINDS, not from its declared
 * `chapters.to`: a volume whose range overshoots the catalog (the Odyssey's
 * dormant paywall declares volume three through 24) must key its mark on the
 * last chapter a reader can actually finish.
 */
export function lastChapterOf(units: readonly ReadingUnit[]): number | null {
  // Backwards scan, not `findLast` (ES2023 — past the BROWSER_FLOOR).
  for (let i = units.length - 1; i >= 0; i--) {
    const unit = units[i];
    if (unit?.kind === "chapter") return unit.number;
  }
  return null;
}

/** The volume a unit belongs to, and everything that volume binds. */
export interface UnitVolume {
  readonly edition: ShelfEdition;
  readonly units: readonly ReadingUnit[];
  /** True when `unit` is the last thing in the volume — where it ends. */
  readonly atEnd: boolean;
}

/**
 * Which cover the reader currently has open, from the book and the unit on
 * screen. `null` for a book the shelf doesn't carry (nothing to mark).
 *
 * The reading view uses this for the end-of-volume "mark as read" control, so
 * it asks the same `shelfUnits` partition the library renders — the alternative
 * (testing the unit against `edition.chapters`) would disagree with the shelf
 * about matter, which belongs to a volume by anchor, not by number.
 */
export function volumeOfUnit(
  book: CatalogBook,
  unit: ReadingUnit,
): UnitVolume | null {
  const editions = editionsFor(book.slug);
  if (editions.length === 0) return null;
  const key = unitSegment(unit);
  const buckets = shelfUnits(book, editions);
  const index = buckets.findIndex((units) =>
    units.some((u) => unitSegment(u) === key),
  );
  const units = buckets[index];
  const edition = editions[index];
  if (units === undefined || edition === undefined) return null;
  return {
    edition,
    units,
    atEnd: unitSegment(units[units.length - 1]!) === key,
  };
}
